import { Box, Link, Stack, Tooltip, Typography } from '@mui/joy';
import { CaretRight } from '@phosphor-icons/react';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { IBreadcrumbsItem } from 'core/types';
import { Color, Direction, Level, TooltipPosition } from 'theme/enums';

const MIN_ITEMS_FOR_TRUNCATION = 5;

interface IProps {
  items: IBreadcrumbsItem[];
}

const Separator = () => (
  <CaretRight weight="bold" style={{ fontSize: 16, flexShrink: 0, opacity: 0.6 }} />
);

const ItemNode = ({ item, isLast }: { item: IBreadcrumbsItem; isLast: boolean }) =>
  item.to ? (
    <Link
      component={RouterLink}
      to={item.to}
      level={Level.BODY_SM}
      sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
    >
      {item.text}
    </Link>
  ) : (
    <Typography
      level={Level.BODY_SM}
      color={Color.NEUTRAL}
      sx={{ flexShrink: 0, whiteSpace: 'nowrap', ...(isLast && { color: 'text.primary' }) }}
    >
      {item.text}
    </Typography>
  );

interface IListProps {
  items: IBreadcrumbsItem[];
  collapsedTooltip?: string;
  collapsedAt?: number;
}

const BreadcrumbList = ({ items, collapsedTooltip, collapsedAt }: IListProps) => (
  <Stack
    direction={Direction.ROW}
    sx={{ alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap', flexShrink: 0 }}
  >
    {items.map((item, index) => (
      <Stack
        key={`${item.text}-${index}`}
        direction={Direction.ROW}
        sx={{ alignItems: 'center', gap: 0.5, fontWeight: 500 }}
      >
        {index === collapsedAt && collapsedTooltip ? (
          <Tooltip title={collapsedTooltip} placement={TooltipPosition.BOTTOM_START}>
            <Typography level={Level.BODY_SM} color={Color.PRIMARY} sx={{ cursor: 'help' }}>
              {item.text}
            </Typography>
          </Tooltip>
        ) : (
          <ItemNode item={item} isLast={index === items.length - 1} />
        )}
        {index < items.length - 1 && <Separator />}
      </Stack>
    ))}
  </Stack>
);

export const PartDetailsBreadcrumbs = ({ items }: IProps) => {
  const [isOverflowing, setIsOverflowing] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;

    if (!container || !measure) {
      return;
    }

    const check = () => {
      setIsOverflowing(measure.scrollWidth > container.clientWidth + 1);
    };

    check();

    const observer = new ResizeObserver(check);

    observer.observe(container);

    return () => observer.disconnect();
  }, [items]);

  const truncated = useMemo(() => {
    if (items.length < MIN_ITEMS_FOR_TRUNCATION) {
      return null;
    }

    const hidden = items.slice(2, -2);
    const tooltip = hidden.map((item) => item.text).join(' > ');

    return {
      items: [
        items[0],
        items[1],
        { text: '...' },
        items[items.length - 2],
        items[items.length - 1],
      ],
      tooltip,
    };
  }, [items]);

  const showTruncated = isOverflowing && truncated !== null;

  return (
    <Box ref={containerRef} sx={{ minWidth: 0, overflow: 'hidden', flex: 1 }}>
      <Box
        ref={measureRef}
        aria-hidden
        sx={{
          position: 'absolute',
          visibility: 'hidden',
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
        }}
      >
        <BreadcrumbList items={items} />
      </Box>

      {showTruncated ? (
        <BreadcrumbList
          items={truncated.items}
          collapsedAt={2}
          collapsedTooltip={truncated.tooltip}
        />
      ) : (
        <BreadcrumbList items={items} />
      )}
    </Box>
  );
};
